import { Download, FileDown, Loader2, Printer, X } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { mandarParaImpressora } from "@/features/mesa-qr/imprimirNaTermica";

import { baixarPdfLinks, baixarPngLink, imprimirLinks, qrComoDataUri } from "./api";

const BOTAO = "h-10 rounded-xl border border-border text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-muted disabled:opacity-50";

/**
 * QR Code de UM link de divulgação (dono, 26/10/2026), na mesma ideia do QR
 * das mesas: o código com o nome do link no meio, o endereço e os botões de
 * imprimir na impressora do caixa e baixar a imagem e o PDF. O QR abre o MESMO
 * link — cliques, pedidos e faturado de quem escaneia contam neste quadro.
 */
export default function ModalQrLink({ link, titulo, onFechar }) {
  const [imagem, setImagem] = useState(null);
  const [erro, setErro] = useState(false);
  const [baixando, setBaixando] = useState(null);
  const token = link?.track_token;

  useEffect(() => {
    if (!token) return undefined;
    let vivo = true;
    setImagem(null);
    setErro(false);
    // 404 = o link foi apagado (ex.: noutra aba) e esta página ficou para trás
    qrComoDataUri(token).then((uri) => vivo && setImagem(uri))
      .catch((e) => vivo && setErro(e?.response?.status === 404 ? "apagado" : "falha"));
    return () => { vivo = false; };
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    const esc = (e) => { if (e.key === "Escape") onFechar(); };
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [token, onFechar]);

  if (!link) return null;

  const imprimir = async () => {
    setBaixando("imprimir");
    await mandarParaImpressora(() => imprimirLinks([token]));
    setBaixando(null);
  };

  const baixar = async (tipo) => {
    setBaixando(tipo);
    try {
      if (tipo === "pdf") await baixarPdfLinks([token], titulo);
      else await baixarPngLink(token, titulo);
    } catch {
      toast.error(tipo === "pdf" ? "Não consegui gerar o PDF." : "Não consegui baixar a imagem.");
    } finally {
      setBaixando(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onFechar} data-e2e="dialog-qr-link">
      <div className="bg-background rounded-2xl w-full max-w-sm max-h-[92dvh] overflow-y-auto border border-border shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2 className="text-base font-bold truncate">QR Code — {titulo}</h2>
            <p className="text-xs text-muted-foreground">Use em panfletos, cartazes e embalagens.</p>
          </div>
          <button type="button" onClick={onFechar} className="ml-auto p-2 rounded-lg hover:bg-muted" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 flex flex-col items-center">
          {imagem ? (
            <img src={imagem} alt={`QR Code do link ${titulo}`} className="w-56 h-56 rounded-xl bg-white p-2" data-e2e="qr-link-imagem" />
          ) : erro === "apagado" ? (
            <p className="text-sm text-muted-foreground py-16 text-center" data-e2e="qr-link-apagado">
              Este link não existe mais.<br />Atualize a página para ver os links de agora.
            </p>
          ) : erro ? (
            <p className="text-sm text-muted-foreground py-16 text-center">
              Não consegui gerar o código.<br />Feche e tente de novo.
            </p>
          ) : (
            <div className="w-56 h-56 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          )}
          <code className="mt-3 max-w-full truncate rounded-lg bg-muted px-2.5 py-1.5 text-xs">{link.link}</code>
          <p className="mt-3 text-[11px] text-muted-foreground text-center leading-snug">
            O QR abre este mesmo link: quem escanear conta nos cliques, pedidos e faturado deste quadro.
          </p>
        </div>

        <div className="px-5 pb-5 space-y-2">
          <button type="button" onClick={imprimir} disabled={!imagem || !!baixando} data-e2e="qr-link-imprimir"
            className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-2 disabled:opacity-50">
            {baixando === "imprimir" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Printer className="w-4 h-4" />} Imprimir na impressora
          </button>
          <div className="flex gap-2">
          <button type="button" onClick={() => baixar("png")} disabled={!imagem || !!baixando} className={`flex-1 ${BOTAO}`}
            data-e2e="qr-link-baixar-imagem">
            {baixando === "png" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />} Baixar imagem
          </button>
          <button type="button" onClick={() => baixar("pdf")} disabled={!imagem || !!baixando} className={`flex-1 ${BOTAO}`}
            data-e2e="qr-link-baixar-pdf">
            {baixando === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} Baixar PDF
          </button>
          </div>
        </div>
      </div>
    </div>
  );
}
